import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { changeReasonKeys } from "@/lib/query";
import type { ChangeReasonCreateInput, ChangeReasonUpdateInput } from "@/schemas/change-reasons";

export interface ChangeReason {
  id: string;
  name: string;
  description: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export function useChangeReasons(enabled = true) {
  return useQuery({
    queryKey: changeReasonKeys.all,
    queryFn: async (): Promise<ChangeReason[]> => {
      const res = await fetch("/api/change-reasons");
      if (!res.ok) throw new Error("Failed to fetch change reasons");
      const json = await res.json();
      return json.data;
    },
    enabled,
  });
}

export function useCreateChangeReason() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ChangeReasonCreateInput) => {
      const res = await fetch("/api/change-reasons", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error("Create failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: changeReasonKeys.all });
      toast.success("Change reason created");
    },
    onError: () => toast.error("Failed to create change reason"),
  });
}

export function useUpdateChangeReason(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ChangeReasonUpdateInput) => {
      const res = await fetch(`/api/change-reasons/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error("Update failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: changeReasonKeys.all });
      toast.success("Change reason updated");
    },
    onError: () => toast.error("Failed to update change reason"),
  });
}

export function useReorderChangeReason() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, direction }: { id: string; direction: "up" | "down" }) => {
      const res = await fetch(`/api/change-reasons/${id}/reorder`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ direction }),
      });
      if (!res.ok) throw new Error("Reorder failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: changeReasonKeys.all });
    },
    onError: () => toast.error("Failed to reorder change reason"),
  });
}

export function useDeleteChangeReason() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/change-reasons/${id}`, { method: "DELETE" });
      if (!res.ok) {
        // The refusal carries the reason — rows still using it, a built-in
        // value — and saying "Delete failed" instead left the admin with no
        // idea what to fix, while the next tab along explained itself.
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(body?.error ?? "Delete failed");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: changeReasonKeys.all });
      toast.success("Change reason deleted");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
